import _ from 'lodash';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { getGlobalClasses } from '../../themes';
import ScheduleConfigContainer from '../../containers/ScheduleConfig';
import FormComponent from '../../components/FormComponent';
import { getUserAssignmentTypeDisplay } from '../../libs/user';

function formatSeedDate(scheduleStartDate) {
	const date = new Date(scheduleStartDate + 'T00:00:00');
	const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
	const dayPart = date.toLocaleDateString('en-US', { weekday: 'long' });
	return `${datePart} (${dayPart})`;
}

function getWorkDays(config) {
	if (config.isTypeA) return config.aCycleDays || [];
	if (config.isTypeB) return config.bCycleDays || [];
	if (config.isTypeC) return config.cCycleDays || [];
	return config.cycleDays || [];
}

function getTypeLabel(config) {
	if (config.isTypeA) return 'A';
	if (config.isTypeB) return 'B';
	if (config.isTypeC) return 'C';
	return '—';
}

const styles = (theme) => ({
	...getGlobalClasses(theme),
	sectionHeadline: {
		fontSize: 20,
		fontWeight: 600,
		marginTop: 32,
		marginBottom: 12,
		color: '#333',
	},
	tableCard: {
		marginBottom: 32,
		overflowX: 'auto',
	},
	headerCell: {
		fontWeight: 700,
		backgroundColor: '#f5f5f5',
		whiteSpace: 'nowrap',
	},
	cycleDayHeader: {
		fontWeight: 700,
		backgroundColor: '#f5f5f5',
		textAlign: 'center',
		padding: '6px 4px',
		minWidth: 28,
	},
	workCell: {
		color: '#2e7d32',
		fontWeight: 700,
		textAlign: 'center',
		padding: '6px 4px',
	},
	offCell: {
		color: '#ddd',
		textAlign: 'center',
		padding: '6px 4px',
	},
	infoCell: {
		whiteSpace: 'nowrap',
		padding: '6px 12px',
	},
	tableRow: {
		'&:nth-child(even)': {
			backgroundColor: '#fafafa',
		},
	},
});

function CycleGroupTable({ configs, dayCycle, classes }) {
	const cycleDayIndices = _.range(dayCycle);

	return (
		<Paper className={classes.tableCard}>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell className={classes.headerCell}>Schedule</TableCell>
						<TableCell className={classes.headerCell}>Type</TableCell>
						<TableCell className={classes.headerCell}>State</TableCell>
						<TableCell className={classes.headerCell}>Seed Date</TableCell>
						{cycleDayIndices.map((day) => (
							<TableCell key={day} className={classes.cycleDayHeader}>
								{day}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{configs.map((config) => {
						const workDays = getWorkDays(config);
						return (
							<TableRow key={config.userAssignmentTypeId} className={classes.tableRow}>
								<TableCell className={classes.infoCell}>
									{getUserAssignmentTypeDisplay({
										userAssignmentTypeId: config.userAssignmentTypeId,
									})}
								</TableCell>
								<TableCell className={classes.infoCell}>{getTypeLabel(config)}</TableCell>
								<TableCell className={classes.infoCell}>{config.state || '—'}</TableCell>
								<TableCell className={classes.infoCell}>
									{config.scheduleStartDate ? formatSeedDate(config.scheduleStartDate) : '—'}
								</TableCell>
								{cycleDayIndices.map((day) => {
									const isWork = workDays.indexOf(day) >= 0;
									return (
										<TableCell
											key={day}
											className={isWork ? classes.workCell : classes.offCell}
										>
											{isWork ? '✓' : '—'}
										</TableCell>
									);
								})}
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</Paper>
	);
}

class ScheduleConfig extends FormComponent {
	constructor(props) {
		super(props);
		this.state = {
			scheduleConfigs: [],
		};
	}

	componentDidMount() {
		const { getScheduleConfigs, showErrors } = this.props;
		getScheduleConfigs({
			callback: (err, r) => {
				if (err) return showErrors(err);
				this.setState({ scheduleConfigs: r.scheduleConfigs });
			},
		});
	}

	render() {
		const { classes } = this.props;
		const { scheduleConfigs } = this.state;

		// Group by dayCycle, preserving insertion order
		const groups = {};
		scheduleConfigs.forEach((config) => {
			const key = config.dayCycle;
			if (!groups[key]) groups[key] = [];
			groups[key].push(config);
		});

		return (
			<Fragment>
				<div className={classes.globalMainHeadline}>Schedule Config</div>

				<Grid container spacing={2}>
					<Grid item xs={12}>
						{Object.entries(groups).map(([dayCycle, configs]) => (
							<Fragment key={dayCycle}>
								<Typography className={classes.sectionHeadline}>
									{dayCycle}-Day Cycle
								</Typography>
								<CycleGroupTable
									configs={configs}
									dayCycle={Number(dayCycle)}
									classes={classes}
								/>
							</Fragment>
						))}
					</Grid>
				</Grid>
			</Fragment>
		);
	}
}

ScheduleConfig.propTypes = {
	classes: PropTypes.object.isRequired,
	getScheduleConfigs: PropTypes.func.isRequired,
	showErrors: PropTypes.func.isRequired,
};

export default _.flow([withStyles(styles), ScheduleConfigContainer])(ScheduleConfig);
